import React, { useState, useMemo } from 'react';
import {
  Objective,
  ObjectiveStatus,
  Review,
  ReviewType,
  ReviewCycle,
  RatingScale,
} from '../../types/pms';
import ReviewCycleSelector from './ReviewCycleSelector';
import ProgressBar from './ProgressBar';
import { getRatingLabel } from '../../services/pmsRatingCalculator';
import { calculatePercentage, formatChartPercentage } from './hrAnalyticsCharts';
import './PerformanceReports.css';

type ReportTab = 'team-progress' | 'distribution' | 'completion';

interface TeamProgressRow {
  employeeId: string;
  employeeName: string;
  objectiveCount: number;
  averageProgress: number;
  completedCount: number;
}

interface DistributionRow {
  rating: RatingScale;
  label: string;
  count: number;
  percentage: number;
}

interface CompletionRow {
  status: ObjectiveStatus;
  count: number;
}

interface PerformanceReportsProps {
  /** All objectives for the selected cycle */
  allObjectives: Objective[];
  /** All reviews for the selected cycle */
  allReviews: Review[];
  /** Available review cycles */
  reviewCycles: ReviewCycle[];
  /** Currently selected review cycle ID */
  selectedCycleId: string;
  /** Callback when review cycle changes */
  onCycleChange: (cycleId: string) => void;
}

function buildTeamProgress(objectives: Objective[]): TeamProgressRow[] {
  const map = new Map<string, Objective[]>();
  for (const obj of objectives) {
    const list = map.get(obj.employeeId) || [];
    list.push(obj);
    map.set(obj.employeeId, list);
  }
  return Array.from(map.entries()).map(([employeeId, objs]) => ({
    employeeId,
    employeeName: objs[0].employeeName,
    objectiveCount: objs.length,
    averageProgress:
      objs.length > 0
        ? Math.round(objs.reduce((s, o) => s + o.progressPercentage, 0) / objs.length)
        : 0,
    completedCount: objs.filter((o) => o.status === ObjectiveStatus.COMPLETED).length,
  }));
}

function buildDistribution(reviews: Review[], cycleId: string): DistributionRow[] {
  const endYear = reviews.filter(
    (r) => r.reviewCycleId === cycleId && r.reviewType === ReviewType.END_YEAR && r.overallRating
  );
  const total = endYear.length;
  const counts = new Map<RatingScale, number>();
  for (const r of endYear) {
    if (r.overallRating) counts.set(r.overallRating, (counts.get(r.overallRating) || 0) + 1);
  }
  return [
    RatingScale.OUTSTANDING,
    RatingScale.EXCEEDS_EXPECTATIONS,
    RatingScale.MEETS_EXPECTATIONS,
    RatingScale.NEEDS_IMPROVEMENT,
    RatingScale.UNSATISFACTORY,
  ].map((rating) => ({
    rating,
    label: getRatingLabel(rating),
    count: counts.get(rating) || 0,
    percentage: calculatePercentage(counts.get(rating) || 0, total),
  }));
}

function buildCompletion(objectives: Objective[]): CompletionRow[] {
  const counts = new Map<ObjectiveStatus, number>();
  for (const obj of objectives) {
    counts.set(obj.status, (counts.get(obj.status) || 0) + 1);
  }
  return Object.values(ObjectiveStatus).map((status) => ({
    status,
    count: counts.get(status) || 0,
  }));
}

function toCsv(headers: string[], rows: string[][]): string {
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * PerformanceReports Component
 * Team progress, performance distribution, and objective completion reports with CSV export.
 * Requirements: 16.1, 16.2, 16.3, 16.4, 22.3
 */
const PerformanceReports: React.FC<PerformanceReportsProps> = ({
  allObjectives,
  allReviews,
  reviewCycles,
  selectedCycleId,
  onCycleChange,
}) => {
  const [activeTab, setActiveTab] = useState<ReportTab>('team-progress');

  const teamProgress = useMemo(() => buildTeamProgress(allObjectives), [allObjectives]);
  const distribution = useMemo(() => buildDistribution(allReviews, selectedCycleId), [allReviews, selectedCycleId]);
  const completion = useMemo(() => buildCompletion(allObjectives), [allObjectives]);

  const handleExport = () => {
    let csv = '';
    let filename = '';
    switch (activeTab) {
      case 'team-progress':
        csv = toCsv(
          ['Employee', 'Objectives', 'Avg Progress (%)', 'Completed'],
          teamProgress.map((r) => [r.employeeName, String(r.objectiveCount), String(r.averageProgress), String(r.completedCount)])
        );
        filename = 'team-progress-report.csv';
        break;
      case 'distribution':
        csv = toCsv(
          ['Rating', 'Count', 'Percentage (%)'],
          distribution.map((r) => [r.label, String(r.count), String(r.percentage)])
        );
        filename = 'performance-distribution-report.csv';
        break;
      case 'completion':
        csv = toCsv(
          ['Status', 'Count'],
          completion.map((r) => [r.status, String(r.count)])
        );
        filename = 'objective-completion-report.csv';
        break;
    }
    downloadCsv(filename, csv);
  };

  const tabs: { key: ReportTab; label: string }[] = [
    { key: 'team-progress', label: 'Team Progress' },
    { key: 'distribution', label: 'Performance Distribution' },
    { key: 'completion', label: 'Objective Completion' },
  ];

  return (
    <div className="pms-reports">
      <div className="pms-reports__header">
        <h1 className="pms-reports__title">Performance Reports</h1>
        <div className="pms-reports__header-actions">
          <ReviewCycleSelector
            cycles={reviewCycles}
            selectedCycleId={selectedCycleId}
            onChange={onCycleChange}
          />
          <button className="pms-reports__export-btn" onClick={handleExport}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="pms-reports__tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`pms-reports__tab ${activeTab === tab.key ? 'pms-reports__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Team Progress Report (Req 16.1) */}
      {activeTab === 'team-progress' && (
        <div className="pms-reports__table-wrapper">
          <table className="pms-reports__table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Objectives</th>
                <th>Avg Progress</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {teamProgress.length === 0 ? (
                <tr><td colSpan={4} className="pms-reports__empty-cell">No data available.</td></tr>
              ) : (
                teamProgress.map((row) => (
                  <tr key={row.employeeId}>
                    <td className="pms-reports__name-cell">{row.employeeName}</td>
                    <td>{row.objectiveCount}</td>
                    <td><ProgressBar value={row.averageProgress} size="sm" /></td>
                    <td>{row.completedCount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Performance Distribution Report (Req 16.2) */}
      {activeTab === 'distribution' && (
        <div className="pms-reports__table-wrapper">
          <table className="pms-reports__table">
            <thead>
              <tr>
                <th>Rating</th>
                <th>Count</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {distribution.map((row) => (
                <tr key={row.rating}>
                  <td className="pms-reports__name-cell">{row.label} ({row.rating})</td>
                  <td>{row.count}</td>
                  <td>
                    <div className="pms-reports__bar-cell">
                      <div className="pms-reports__mini-bar-track">
                        <div className="pms-reports__mini-bar" style={{ width: `${row.percentage}%` }} />
                      </div>
                      <span>{formatChartPercentage(row.percentage)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Objective Completion Report (Req 16.3) */}
      {activeTab === 'completion' && (
        <div className="pms-reports__table-wrapper">
          <table className="pms-reports__table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {completion.map((row) => (
                <tr key={row.status}>
                  <td className="pms-reports__name-cell">{row.status.replace('_', ' ')}</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PerformanceReports;
